import { useEffect, useState } from 'react'
import { PageHeader } from '../../../components/PageHeader'
import { GameTitle } from '../../../components/GameTitle'
import { ChevronRight } from '../../../components/ChevronRight'
import { BackgroundTitle } from '../../../components/BackgroundTitle'
import {
  buildPromptText, fetchThreeStepWeeklyWinners, threeStepArchiveDateLabel,
  type ThreeStepWeeklyWinnerRow,
} from '../api/weekly'
import { readError } from '../../../lib/errors'
import { useT } from '../../../i18n'

interface Props {
  onBack: () => void
  onViewWeek: (eventId: string) => void
}

/**
 * Chronological list of finalized Three Step weeks. Each row links to
 * that week's winners screen, which shows the full leaderboard of
 * three-step solutions for that prompt.
 */
export function ThreeStepWeeklyArchiveScreen({ onBack, onViewWeek }: Props) {
  const t = useT()
  const [weeks, setWeeks] = useState<ThreeStepWeeklyWinnerRow[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    // No week is excluded. A week that handed out a trophy must be
    // listed here, otherwise tapping that trophy opens a week the
    // archive says doesn't exist. The RPC's finalized check already
    // keeps the in-progress week out; excluding `lastWeek` as well
    // also hid the most recently settled week.
    fetchThreeStepWeeklyWinners()
      .then(rows => { if (!cancelled) setWeeks(rows) })
      .catch(e => { if (!cancelled) setError(readError(e)) })
    return () => { cancelled = true }
  }, [])

  return (
    <div className="screen">
      <BackgroundTitle text={t('Three Step Solution Archive')} color="#0EAE93" />
      <PageHeader title={t('Three Step · Archive')} onBack={onBack} />

      <div className="flex-1 px-5 py-5 space-y-3 overflow-y-auto pb-6 relative z-10">
        <div className="flex items-center gap-3 mb-2">
          <img src="/games/three-step.webp" alt="Three Step Solution"
               className="w-12 h-12 rounded-2xl object-cover shadow-xs shrink-0" />
          <div>
            <GameTitle game="three-step" size="sm" />
            <p className="text-xs text-brand-muted">
              {t('The winning three-step answer from every past week')}
            </p>
          </div>
        </div>

        {error && (
          <div className="card p-3 bg-brand-danger/10 border border-brand-danger/30">
            <p className="text-sm text-brand-danger">{error}</p>
          </div>
        )}

        {weeks === null && (
          <>
            <div className="h-16 rounded-2xl bg-brand-elevated animate-pulse" />
            <div className="h-16 rounded-2xl bg-brand-elevated animate-pulse" />
            <div className="h-16 rounded-2xl bg-brand-elevated animate-pulse" />
          </>
        )}

        {weeks?.length === 0 && (
          <p className="text-sm text-brand-muted text-center py-8">
            {t('No finalized weeks yet.')}
          </p>
        )}

        {weeks && weeks.map(w => (
          <button
            key={w.event_id}
            onClick={() => onViewWeek(w.event_id)}
            className="w-full bg-game-three-step-light border-2 border-game-three-step-dark
                       rounded-2xl shadow-card p-4 flex items-center gap-3 text-left
                       active:scale-[0.98] transition-transform"
          >
            <div className="flex-1 min-w-0">
              <p className="text-[10px] font-semibold text-game-three-step-dark
                            uppercase tracking-widest">
                {threeStepArchiveDateLabel(w.week_start)}
              </p>
              <p className="text-sm font-semibold text-brand-text truncate">
                {buildPromptText(w.action_a, w.action_b, t)}
              </p>
              <p className="text-xs text-brand-muted truncate mt-0.5">
                Winner: {w.author_name} · {w.total_votes} vote{w.total_votes === 1 ? '' : 's'}
              </p>
            </div>
            <ChevronRight color="#0EAE93" />
          </button>
        ))}
      </div>
    </div>
  )
}
